import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from './auth';
import { StorageService } from './storage';
import { ToastService } from './toast';
import { MenuService } from './menu';
import { PartnerKycService } from './partner-kyc';
import { AdminNotificationService } from './admin-notification';

/** 15 minutes of continuous user INACTIVITY ends the session (not 15 minutes after login). */
const IDLE_LIMIT_MS = 15 * 60 * 1000;
/** High-frequency events (mousemove/scroll) only persist a new timestamp this often. */
const WRITE_THROTTLE_MS = 5_000;
/** One low-cost deadline check on this cadence — no timer is ever re-armed per event. */
const CHECK_EVERY_MS = 30_000;
/** Shared across tabs via localStorage, so typing in one tab keeps every tab alive. */
const ACTIVITY_KEY = 'lastActivityAt';

/**
 * Inactivity auto-logout for the authenticated shell.
 *
 * Design constraints this deliberately meets:
 *  - ONE interval + ONE set of listeners for the whole app (started by Layout, which mounts
 *    once per session — route changes never touch it). start() is idempotent.
 *  - Activity is a throttled timestamp write, never a timer reset — so mousemove cannot
 *    churn timers, and the deadline math is immune to setInterval drift and laptop sleep
 *    (on wake, the next check compares wall-clock times and acts correctly).
 *  - Cross-tab: the timestamp lives in localStorage, so activity in ANY tab counts for all.
 *    When this tab times out it clears storage; other tabs' `storage` listener (in Layout)
 *    sends them to login silently — the "session expired" message appears exactly once.
 *  - Complements, never replaces, real security: the JWT still expires server-side and the
 *    logout call revokes the refresh token, so an idle client cannot outlive its tokens.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private auth = inject(AuthService);
  private storage = inject(StorageService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private menuService = inject(MenuService);
  private kycService = inject(PartnerKycService);
  private notificationService = inject(AdminNotificationService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private checkTimer: ReturnType<typeof setInterval> | null = null;
  private lastActivity = 0;
  private lastWrite = 0;
  private loggingOut = false;

  /** Bound once so add/removeEventListener refer to the same functions (no orphan listeners). */
  private readonly onActivity = () => this.recordActivity();
  private readonly onVisible = () => {
    // A background tab's interval is throttled by the browser; when the user returns,
    // decide immediately instead of showing up to 30s of stale authenticated UI.
    if (document.visibilityState === 'visible') this.check();
  };

  /** What counts as "meaningful user activity". Scroll doesn't bubble → capture phase. */
  private static readonly EVENTS = [
    'mousemove',
    'mousedown',
    'keydown',
    'wheel',
    'scroll',
    'touchstart',
  ] as const;

  /** Called by the shell when an authenticated session is on screen. Idempotent. */
  start(): void {
    if (!this.isBrowser || this.checkTimer) return;
    this.loggingOut = false;
    this.recordActivity(true);
    for (const ev of IdleTimeoutService.EVENTS) {
      document.addEventListener(ev, this.onActivity, { capture: true, passive: true });
    }
    document.addEventListener('visibilitychange', this.onVisible);
    this.checkTimer = setInterval(() => this.check(), CHECK_EVERY_MS);
  }

  /** Detach everything. Safe to call repeatedly; Layout calls it on destroy. */
  stop(): void {
    if (!this.isBrowser) return;
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
    for (const ev of IdleTimeoutService.EVENTS) {
      document.removeEventListener(ev, this.onActivity, { capture: true });
    }
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  private recordActivity(force = false): void {
    const now = Date.now();
    this.lastActivity = now;
    // localStorage writes are what other tabs see; throttled so mousemove stays cheap.
    if (force || now - this.lastWrite >= WRITE_THROTTLE_MS) {
      this.lastWrite = now;
      this.storage.setLocal(ACTIVITY_KEY, String(now));
    }
  }

  private check(): void {
    if (this.loggingOut) return;
    // Session already ended (manual logout / another tab / interceptor cleanup):
    // Layout tears the shell down and calls stop(); nothing for us to do.
    if (!this.storage.getLocal('token')) {
      this.stop();
      return;
    }
    // The freshest activity WINS, wherever it happened — this tab (memory) or another (storage).
    const shared = Number(this.storage.getLocal(ACTIVITY_KEY)) || 0;
    const last = Math.max(this.lastActivity, shared);
    if (last && Date.now() - last >= IDLE_LIMIT_MS) this.logoutForInactivity();
  }

  private logoutForInactivity(): void {
    this.loggingOut = true;
    // Revoke the refresh token server-side while it is still in storage — an idle logout
    // that leaves a live refresh token behind isn't a logout. Best-effort: the local
    // cleanup below must happen even if the network call fails.
    this.auth.logout().subscribe({ next: () => {}, error: () => {} });

    this.storage.clearAll();
    this.menuService.reset();
    this.kycService.reset();
    this.notificationService.reset();
    this.stop();

    this.toast.warning(
      'Your session expired due to 15 minutes of inactivity. Please log in again.',
      'Session expired',
    );
    this.router.navigate(['/login']);
  }
}
